import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  alpha,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  ContentPaste as ContentPasteIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  Store as StoreIcon,
  CleaningServices as CleanIcon,
  ExitToApp as ExitToAppIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "../../components/layout/PageHeader";
import { branchAPI } from "../../services/branchAPI";
import { salesPointAPI } from "../../services/salesPointAPI";
import { categoryAPI } from "../../services/categoryAPI";
import { productSalesPointAPI } from "../../services/productSalesPointAPI";
import { UNIT_OPTIONS } from "../../services/productAPI";
import { useAuthStore } from "../../stores/authStore";
import { useRBAC } from "../../hooks/useRBAC";
import { tokens } from "../../theme";

export interface ExcelRowItem {
  id: string;
  name: string;
  category: string;
  price: string;
  gst: string;
  type: "B" | "F";
  sku: string;
  unit: string;
  hsn: string;
  productType: "PREPARED" | "GOODS";
  purchasePrice: string;
  status: "idle" | "ready" | "saved" | "error";
  errorMessage?: string;
}

const DEFAULT_GST_OPTIONS = ["0", "5", "12", "18", "28", "40"];
const createBlankRow = (index = 0): ExcelRowItem => ({
  id: `row-${Date.now()}-${Math.random().toString(36).substr(2, 6)}-${index}`,
  name: "",
  category: "",
  price: "",
  gst: "5",
  type: "B",
  sku: "",
  unit: "PCS",
  hsn: "21069099",
  productType: "PREPARED",
  purchasePrice: "",
  status: "idle",
});

const ProductExcelEntryPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { canChangePosScope } = useRBAC();
  const canChangeScope = canChangePosScope();

  const urlBranchId = searchParams.get("branchId") || "";
  const urlSalesPointId = searchParams.get("salesPointId") || "";

  const [selectedBranch, setSelectedBranch] = useState(
    urlBranchId || user?.branchId || ""
  );
  const [selectedSalesPoint, setSelectedSalesPoint] = useState(
    urlSalesPointId || user?.salesPointId || ""
  );

  const salesPointId = canChangeScope
    ? selectedSalesPoint
    : user?.salesPointId || "";

  // 10 initial rows
  const [rows, setRows] = useState<ExcelRowItem[]>(() =>
    Array.from({ length: 8 }, (_, i) => createBlankRow(i))
  );

  const [saving, setSaving] = useState(false);
  const [pasteModalOpen, setPasteModalOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [alert, setAlert] = useState<{
    type: "success" | "error" | "info";
    message: string;
  } | null>(null);

  // Queries
  const { data: branches = [] } = useQuery({
    queryKey: ["branches", "all"],
    queryFn: () => branchAPI.getAllBranches(),
    enabled: canChangeScope,
  });

  const { data: salesPoints = [] } = useQuery({
    queryKey: ["sales_points", "all"],
    queryFn: () => salesPointAPI.getAllSalesPoints(),
    enabled: canChangeScope,
  });

  const { data: userSalesPoint } = useQuery({
    queryKey: ["sales_point", salesPointId],
    queryFn: () => salesPointAPI.getSalesPointById(salesPointId),
    enabled: !!salesPointId && !canChangeScope,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["categories", "all-quick-entry"],
    queryFn: () => categoryAPI.getCategories({ pageSize: 500, is_active: true }),
  });
  const existingCategories = categoriesData?.data ?? [];

  const filteredSalesPoints = selectedBranch
    ? salesPoints.filter((sp) => sp.branchId === selectedBranch)
    : salesPoints;

  // Auto-select first sales point if none selected
  useEffect(() => {
    if (!selectedSalesPoint && filteredSalesPoints.length > 0) {
      setSelectedSalesPoint(filteredSalesPoints[0].id);
    }
  }, [selectedSalesPoint, filteredSalesPoints]);

  // Sync selected branch if salesPoint changes
  useEffect(() => {
    if (selectedSalesPoint && salesPoints.length) {
      const sp = salesPoints.find((s) => s.id === selectedSalesPoint);
      if (sp && sp.branchId && sp.branchId !== selectedBranch) {
        setSelectedBranch(sp.branchId);
      }
    }
  }, [selectedSalesPoint, salesPoints, selectedBranch]);

  // Update row status depending on inputs
  const computeRowStatus = (row: ExcelRowItem): "idle" | "ready" | "saved" | "error" => {
    if (row.status === "saved") return "saved";
    if (row.status === "error") return "error";
    const nameValid = row.name.trim().length > 0;
    const priceValid = row.price.trim().length > 0 && Number(row.price) >= 0;
    if (nameValid && priceValid) return "ready";
    return "idle";
  };

  const handleCellChange = (
    id: string,
    field: keyof ExcelRowItem,
    value: string
  ) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const updated = { ...row, [field]: value };
        if (updated.status === "error") {
          updated.status = "idle";
          updated.errorMessage = undefined;
        }
        updated.status = computeRowStatus(updated);
        return updated;
      })
    );
  };

  const handleAddRows = (count = 1) => {
    setRows((prev) => [
      ...prev,
      ...Array.from({ length: count }, (_, i) => createBlankRow(prev.length + i)),
    ]);
  };

  const handleRemoveRow = (id: string) => {
    setRows((prev) => {
      const filtered = prev.filter((r) => r.id !== id);
      return filtered.length ? filtered : [createBlankRow(0)];
    });
  };

  const handleClearEmptyRows = () => {
    setRows((prev) => {
      const nonEmpty = prev.filter((r) => r.name.trim() || r.price.trim());
      return nonEmpty.length ? nonEmpty : [createBlankRow(0)];
    });
  };

  // Keyboard navigation & auto-append
  const handleKeyDown = (
    e: React.KeyboardEvent,
    rowIndex: number,
    colIndex: number
  ) => {
    // If Tab on the last cell of the last row (Cost Price), add a new row
    if (e.key === "Tab" && !e.shiftKey && rowIndex === rows.length - 1 && colIndex === 10) {
      handleAddRows(1);
    }
  };

  // Paste handler: parses Tab-Separated Values (TSV) from Excel
  const processPastedText = (text: string) => {
    if (!text.trim()) return;
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    const newItems: ExcelRowItem[] = [];

    // Header check
    let startIdx = 0;
    const firstLineLower = lines[0].toLowerCase();
    if (
      firstLineLower.includes("name") ||
      firstLineLower.includes("category") ||
      firstLineLower.includes("price")
    ) {
      startIdx = 1;
    }

    for (let i = startIdx; i < lines.length; i++) {
      const cols = lines[i].split("\t").map((c) => c.trim());
      if (cols.length === 0 || !cols[0]) continue;

      const [
        name = "",
        category = "",
        price = "",
        gst = "5",
        type = "B",
        sku = "",
        unit = "PCS",
        hsn = "21069099",
        productType = "PREPARED",
        purchasePrice = "",
      ] = cols;

      const item: ExcelRowItem = {
        id: `pasted-${Date.now()}-${i}`,
        name,
        category,
        price,
        gst: gst.replace(/[^0-9.]/g, "") || "5",
        type: type.toUpperCase() === "F" ? "F" : "B",
        sku,
        unit: unit.toUpperCase() || "PCS",
        hsn,
        productType: productType.toUpperCase() === "GOODS" ? "GOODS" : "PREPARED",
        purchasePrice,
        status: "idle",
      };
      item.status = computeRowStatus(item);
      newItems.push(item);
    }

    if (newItems.length > 0) {
      setRows((prev) => {
        const withoutEmpty = prev.filter((r) => r.name.trim() || r.price.trim());
        return [...withoutEmpty, ...newItems];
      });
      setAlert({
        type: "info",
        message: `Parsed ${newItems.length} row(s) from clipboard. Verify and click Save.`,
      });
    }
  };

  const handleGlobalPaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData("text/plain");
    if (text && text.includes("\t")) {
      e.preventDefault();
      processPastedText(text);
    }
  };

  // Submit valid items to backend
  const handleSaveValidRows = async (navigateAfter = false) => {
    if (!salesPointId) {
      setAlert({
        type: "error",
        message: "Please select a Destination Sales Point (Department) first.",
      });
      return;
    }

    const readyRows = rows.filter(
      (r) => r.name.trim().length > 0 && r.price.trim().length > 0 && r.status !== "saved"
    );

    if (readyRows.length === 0) {
      setAlert({
        type: "error",
        message:
          "No new items to save. Please fill in Name and Price for each item.",
      });
      return;
    }

    setSaving(true);
    setAlert(null);

    // Prepare rows for backend (matches Shah Mastana column mapper)
    const payloadRows = readyRows.map((r) => ({
      rowId: r.id,
      Name: r.name.trim(),
      Category: r.category.trim() || "General",
      Price: Number(r.price) || 0,
      "GST%": Number(r.gst) || 0,
      Type: r.type,
      SKU: r.sku.trim() || undefined,
      Unit: r.unit.trim() || "PCS",
      HSN: r.hsn.trim() || undefined,
      ProductType: r.productType,
      PurchasePrice: r.purchasePrice ? Number(r.purchasePrice) : undefined,
      WorkCategories: parentWorkCategory,
    }));

    try {
      const summary = await productSalesPointAPI.importRows(
        salesPointId,
        payloadRows
      );
      await queryClient.invalidateQueries({
        queryKey: ["productSalesPointList"],
      });

      // Update statuses based on backend results
      const failedMap = new Map<string, string>();
      summary.results.forEach((res) => {
        if (res.status === "failed") {
          failedMap.set(res.name.toLowerCase().trim(), res.reason || "Failed");
        }
      });

      setRows((prev) =>
        prev.map((r) => {
          const nameKey = r.name.toLowerCase().trim();
          if (failedMap.has(nameKey)) {
            return {
              ...r,
              status: "error",
              errorMessage: failedMap.get(nameKey),
            };
          }
          if (r.name.trim() && r.price.trim()) {
            return {
              ...r,
              status: "saved",
            };
          }
          return r;
        })
      );

      if (summary.failed === 0) {
        if (navigateAfter) {
          const query = salesPointId
            ? `?salesPointId=${encodeURIComponent(salesPointId)}&branchId=${encodeURIComponent(selectedBranch)}`
            : "";
          navigate(`/product-list${query}`);
          return;
        }
        setAlert({
          type: "success",
          message: `All ${summary.created + summary.updated} products saved successfully into ${currentSalesPointName}!`,
        });
      } else {
        setAlert({
          type: "info",
          message: `Saved ${summary.created} added, ${summary.updated} updated. ${summary.failed} row(s) had errors.`,
        });
      }
    } catch (err: any) {
      setAlert({
        type: "error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to save products.",
      });
    } finally {
      setSaving(false);
    }
  };

  const readyCount = rows.filter(
    (r) => r.name.trim().length > 0 && r.price.trim().length > 0 && r.status !== "saved"
  ).length;

  const savedCount = rows.filter((r) => r.status === "saved").length;

  const activeSalesPoint =
    salesPoints.find((sp) => sp.id === salesPointId) || userSalesPoint;
  const parentWorkCategory = activeSalesPoint?.workstationType || "restaurant";

  const currentSalesPointName =
    activeSalesPoint?.name ||
    "Selected Sales Point";

  return (
    <Box onPaste={handleGlobalPaste}>
      <PageHeader
        title="Add as Excel (Inline Sheet)"
        subtitle="Type or paste items row-by-row like an Excel spreadsheet — fast inline entry with instant save to product list"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Product List", to: "/product-list" },
          { label: "Add as Excel" },
        ]}
        action={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                const query = salesPointId
                  ? `?salesPointId=${encodeURIComponent(salesPointId)}&branchId=${encodeURIComponent(selectedBranch)}`
                  : "";
                navigate(`/product-list${query}`);
              }}
            >
              Back to Product List
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentPasteIcon />}
              onClick={() => setPasteModalOpen(true)}
            >
              Paste from Excel
            </Button>
            <Button
              variant="outlined"
              color="primary"
              disabled={readyCount === 0 || !salesPointId || saving}
              startIcon={<SaveIcon />}
              onClick={() => handleSaveValidRows(false)}
            >
              Save {readyCount > 0 ? `(${readyCount})` : ""}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={readyCount === 0 || !salesPointId || saving}
              startIcon={
                saving ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <ExitToAppIcon />
                )
              }
              onClick={() => handleSaveValidRows(true)}
            >
              {saving
                ? "Saving…"
                : `Save & Move to Products ${readyCount > 0 ? `(${readyCount})` : ""}`}
            </Button>
          </Stack>
        }
      />

      {alert && (
        <Alert
          severity={alert.type}
          sx={{ mb: 2 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      {/* Target Sales Point Selection */}
      <Card
        sx={{
          borderRadius: tokens.radius.md,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "none",
          mb: 2.5,
        }}
      >
        <CardContent sx={{ py: 2, px: 2.5, "&:last-child": { pb: 2 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <StoreIcon color="primary" />
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Destination: {currentSalesPointName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Items will be created in Master Catalog with Work Category: <strong>{parentWorkCategory}</strong> (inherited from Sales Point).
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            {canChangeScope && (
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Branch (Optional)</InputLabel>
                    <Select
                      value={selectedBranch}
                      label="Branch (Optional)"
                      onChange={(e) => {
                        setSelectedBranch(e.target.value);
                      }}
                    >
                      <MenuItem value="">
                        <em>All Branches / Direct Company</em>
                      </MenuItem>
                      {branches.map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Sales Point</InputLabel>
                    <Select
                      value={selectedSalesPoint}
                      label="Sales Point"
                      onChange={(e) => setSelectedSalesPoint(e.target.value)}
                    >
                      {filteredSalesPoints.map((sp) => (
                        <MenuItem key={sp.id} value={sp.id}>
                          {sp.name} {sp.branch?.name ? `(${sp.branch.name})` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Grid Toolbar & Status Summary */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleAddRows(1)}
          >
            +1 Row
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleAddRows(5)}
          >
            +5 Rows
          </Button>
          <Button
            size="small"
            variant="text"
            startIcon={<CleanIcon />}
            onClick={handleClearEmptyRows}
          >
            Clear Empty
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={`${rows.length} total rows`}
            size="small"
            variant="outlined"
          />
          <Chip
            label={`${readyCount} ready to save`}
            size="small"
            color={readyCount > 0 ? "primary" : "default"}
          />
          {savedCount > 0 && (
            <Chip
              label={`${savedCount} saved`}
              size="small"
              color="success"
              icon={<CheckCircleIcon />}
            />
          )}
        </Stack>
      </Stack>

      {/* Spreadsheet Grid */}
      <Card
        sx={{
          borderRadius: tokens.radius.md,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "none",
          overflow: "hidden",
        }}
      >
        <TableContainer sx={{ maxHeight: "calc(100vh - 330px)", overflowX: "hidden" }}>
          <Table stickyHeader size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark" ? "grey.900" : "grey.100",
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    py: 0.75,
                    px: 0.75,
                    borderRight: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell width={36} align="center">
                  #
                </TableCell>
                <TableCell>Name *</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Price (₹) *</TableCell>
                <TableCell>GST%</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>HSN</TableCell>
                <TableCell>Product Type</TableCell>
                <TableCell>Cost Price (₹)</TableCell>
                <TableCell width={42} align="center">
                  Del
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, rIdx) => {
                const isReady = row.status === "ready";
                const isSaved = row.status === "saved";
                const isError = row.status === "error";

                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      bgcolor: isSaved
                        ? alpha(tokens.colors.success.main, 0.05)
                        : isError
                        ? alpha(tokens.colors.error.main, 0.05)
                        : undefined,
                      "& td": {
                        p: 0.5,
                        borderRight: "1px solid",
                        borderColor: "divider",
                      },
                    }}
                  >
                    {/* Row Index & Status */}
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      <Tooltip
                        title={
                          isError
                            ? row.errorMessage || "Error"
                            : isSaved
                            ? "Saved"
                            : isReady
                            ? "Ready to save"
                            : "Enter Name & Price"
                        }
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {isSaved ? (
                            <CheckCircleIcon
                              fontSize="small"
                              sx={{ color: "success.main", fontSize: 18 }}
                            />
                          ) : isError ? (
                            <ErrorIcon
                              fontSize="small"
                              sx={{ color: "error.main", fontSize: 18 }}
                            />
                          ) : isReady ? (
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "primary.main",
                              }}
                            />
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {rIdx + 1}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>

                    {/* Name */}
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="e.g. Cold Coffee"
                        value={row.name}
                        disabled={isSaved}
                        onChange={(e) =>
                          handleCellChange(row.id, "name", e.target.value)
                        }
                        onKeyDown={(e) => handleKeyDown(e, rIdx, 1)}
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: "0.85rem", px: 0.5 },
                        }}
                      />
                    </TableCell>

                    {/* Category */}
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="e.g. Beverages"
                        value={row.category}
                        disabled={isSaved}
                        onChange={(e) =>
                          handleCellChange(row.id, "category", e.target.value)
                        }
                        onKeyDown={(e) => handleKeyDown(e, rIdx, 2)}
                        variant="standard"
                        inputProps={{ list: "category-suggestions" }}
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: "0.85rem", px: 0.5 },
                        }}
                      />
                    </TableCell>

                    {/* Selling Price */}
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        placeholder="50"
                        value={row.price}
                        disabled={isSaved}
                        onChange={(e) =>
                          handleCellChange(row.id, "price", e.target.value)
                        }
                        onKeyDown={(e) => handleKeyDown(e, rIdx, 3)}
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                          sx: {
                            fontSize: "0.85rem",
                            px: 0.5,
                            fontWeight: 600,
                          },
                        }}
                      />
                    </TableCell>

                    {/* GST% */}
                    <TableCell>
                      <Select
                        size="small"
                        fullWidth
                        value={row.gst}
                        disabled={isSaved}
                        onChange={(e) =>
                          handleCellChange(row.id, "gst", String(e.target.value))
                        }
                        variant="standard"
                        disableUnderline
                        sx={{ fontSize: "0.85rem", px: 0.5 }}
                      >
                        {DEFAULT_GST_OPTIONS.map((g) => (
                          <MenuItem key={g} value={g}>
                            {g}%
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>

                    {/* Tax Type: B (Incl) / F (Excl) */}
                    <TableCell>
                      <Select
                        size="small"
                        fullWidth
                        value={row.type}
                        disabled={isSaved}
                        onChange={(e) =>
                          handleCellChange(
                            row.id,
                            "type",
                            e.target.value as "B" | "F"
                          )
                        }
                        variant="standard"
                        disableUnderline
                        sx={{ fontSize: "0.85rem", px: 0.5 }}
                      >
                        <MenuItem value="B">B (Incl)</MenuItem>
                        <MenuItem value="F">F (Excl)</MenuItem>
                      </Select>
                    </TableCell>

                    {/* SKU */}
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Auto"
                        value={row.sku}
                        disabled={isSaved}
                        onChange={(e) =>
                          handleCellChange(row.id, "sku", e.target.value)
                        }
                        onKeyDown={(e) => handleKeyDown(e, rIdx, 6)}
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: "0.82rem", px: 0.5 },
                        }}
                      />
                    </TableCell>

                    {/* Unit */}
                    <TableCell>
                      <Select
                        size="small"
                        fullWidth
                        value={row.unit}
                        disabled={isSaved}
                        onChange={(e) =>
                          handleCellChange(row.id, "unit", String(e.target.value))
                        }
                        variant="standard"
                        disableUnderline
                        sx={{ fontSize: "0.85rem", px: 0.5 }}
                      >
                        {UNIT_OPTIONS.map((u) => (
                          <MenuItem key={u.value} value={u.value}>
                            {u.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>

                    {/* HSN */}
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="21069099"
                        value={row.hsn}
                        disabled={isSaved}
                        onChange={(e) =>
                          handleCellChange(row.id, "hsn", e.target.value)
                        }
                        onKeyDown={(e) => handleKeyDown(e, rIdx, 8)}
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: "0.82rem", px: 0.5 },
                        }}
                      />
                    </TableCell>

                    {/* Product Type */}
                    <TableCell>
                      <Select
                        size="small"
                        fullWidth
                        value={row.productType}
                        disabled={isSaved}
                        onChange={(e) =>
                          handleCellChange(
                            row.id,
                            "productType",
                            e.target.value as "PREPARED" | "GOODS"
                          )
                        }
                        variant="standard"
                        disableUnderline
                        sx={{ fontSize: "0.82rem", px: 0.5 }}
                      >
                        <MenuItem value="PREPARED">PREPARED</MenuItem>
                        <MenuItem value="GOODS">GOODS</MenuItem>
                      </Select>
                    </TableCell>

                    {/* Cost Price */}
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        placeholder="e.g. 30"
                        value={row.purchasePrice}
                        disabled={isSaved}
                        onChange={(e) =>
                          handleCellChange(
                            row.id,
                            "purchasePrice",
                            e.target.value
                          )
                        }
                        onKeyDown={(e) => handleKeyDown(e, rIdx, 10)}
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: "0.82rem", px: 0.5 },
                        }}
                      />
                    </TableCell>

                    {/* Delete action */}
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveRow(row.id)}
                        disabled={isSaved}
                        sx={{ p: 0.25 }}
                      >
                        <DeleteIcon fontSize="small" sx={{ fontSize: 16 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Paste Modal */}
      <Dialog
        open={pasteModalOpen}
        onClose={() => setPasteModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>Paste Excel / Tab-Separated Data</Typography>
            <IconButton size="small" onClick={() => setPasteModalOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: "75vh" }}>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Copy rows directly from Microsoft Excel or Google Sheets (Ctrl+C), paste them into the box below, and click <strong>Import Rows</strong>.
          </Typography>
          <TextField
            multiline
            rows={8}
            fullWidth
            placeholder={"Name\tCategory\tPrice\tGST%\tType\tSKU\tUnit\nCold Coffee\tBeverages\t50\t5\tB\t\tPCS\nVeg Burger\tFast Food\t70\t5\tB\t\tPCS"}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            sx={{ fontFamily: "monospace" }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPasteModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!pasteText.trim()}
            onClick={() => {
              processPastedText(pasteText);
              setPasteText("");
              setPasteModalOpen(false);
            }}
          >
            Import Rows
          </Button>
        </DialogActions>
      </Dialog>

      <datalist id="category-suggestions">
        {existingCategories.map((c: any) => (
          <option key={c.id} value={c.name} />
        ))}
      </datalist>
    </Box>
  );
};

export default ProductExcelEntryPage;
